import React from 'react';
import { connect, useDispatch } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { routerHelperActions } from '@wfp-common/store/routerHelperSlice';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import State from '../app/store/state';
import { AuthorizationSideMenu as SideMenu, AuthorizationSideMenuProps } from '../utils/SideMenu';
import { AuthState, hasFeatureAccess } from '../login/auth';
import { ActionCreators, UpdateStateListType } from './taskAuthorizations';
import { ManagerPermission } from '../permission-profiles/permission';

export enum AuthorizationTab {
    VendorsDetails = 'Vendors',
    Beneficiaries = 'Beneficiaries',
    Transactions = 'Transactions',
    ManagerDetails = 'Users',
    VendorsUsersDetails = 'Vendor Users',
    PartnersDetails = 'Partners',
    PartnersUsersDetails = 'Partner Users',
    Locations = 'Locations',
    ProductsManagement = 'Product Prices',
    ProductsDetails = 'Product Details',
    PermissionProfiles = 'Permission Profiles',
    MobileDevices = 'Mobile Devices',
    BranchCodes = 'Branch Codes',
    ProductPriceVersions = 'Branches in Product Groups',
}

const urlPathForTab = {
    [AuthorizationTab.Transactions]: 'transactions',
    [AuthorizationTab.VendorsDetails]: 'vendors',
    [AuthorizationTab.Beneficiaries]: 'beneficiaries',
    [AuthorizationTab.ManagerDetails]: 'users',
    [AuthorizationTab.VendorsUsersDetails]: 'vendor-users',
    [AuthorizationTab.PartnersDetails]: 'partners',
    [AuthorizationTab.PartnersUsersDetails]: 'partner-users',
    [AuthorizationTab.Locations]: 'locations',
    [AuthorizationTab.ProductsManagement]: 'products',
    [AuthorizationTab.ProductsDetails]: 'product-details',
    [AuthorizationTab.PermissionProfiles]: 'permission-profiles',
    [AuthorizationTab.MobileDevices]: 'mobile-devices',
    [AuthorizationTab.BranchCodes]: 'branch-codes',
    [AuthorizationTab.ProductPriceVersions]: 'product-price-versions',
};
const urlPrefix = '/adjustments';

export function adjustmentsRedirectPath(state: State) {
    const permissionPaths = {
        [ManagerPermission.transactionRequestRead]: 'transactions',
        [ManagerPermission.beneficiariesRead]: 'beneficiaries',
        [ManagerPermission.vendorsRead]: 'vendors',
        [ManagerPermission.managersManagement]: 'users',
    };
    for (const key of Object.keys(permissionPaths)) {
        if (hasFeatureAccess(state.auth, key as ManagerPermission)) {
            return `${urlPrefix}/${permissionPaths[key]}`;
        }
    }
    return `${urlPrefix}/transactions`;
}

interface Props {
    auth: AuthState;
    redirectToPage: (url: string) => void;
    changeListType: (listType: UpdateStateListType) => void;
    pendingAdjustmentActions: {
        transactions?: number;
        beneficiaries?: number;
        vendors?: number;
        users?: number;
        vendorUsers?: number;
        locationActions?: number;
        partners?: number;
        partnerUsers?: number;
        products?: number;
        productSignatures?: number;
        productDetails?: number;
        permissionsProfiles?: number;
        mobileDevices?: number;
        branchCodes?: number;
        productPriceVersions?: number;
    };
    locationToggle: boolean;
    location?: any;
}

type TransactionAuthorizationState = Record<string, never>;

function AuthorizationSideMenu<T extends React.ReactNode>(props: AuthorizationSideMenuProps<T>) {
    return SideMenu(props);
}

class TaskAuthorizationsPage extends React.Component<Props, TransactionAuthorizationState> {
    constructor(props) {
        super(props);
        this.state = {} as TransactionAuthorizationState;
    }

    componentDidMount(): void {
        this.props.changeListType(UpdateStateListType.Requested);
    }

    sidemenuElements(): Array<{ text: AuthorizationTab; counter: number }> {
        const { auth, pendingAdjustmentActions } = this.props;
        const hasTransactionsRead = hasFeatureAccess(auth, ManagerPermission.transactionRequestRead);
        const hasBeneficiaryUpdatesRead = hasFeatureAccess(auth, ManagerPermission.beneficiariesRead);
        const hasLocationUpdatesRead = hasFeatureAccess(auth, ManagerPermission.beneficiariesPost);
        const hasVendorUpdatesRead = hasFeatureAccess(auth, ManagerPermission.vendorsRead);
        const hasVendorUsersUpdatesRead = hasFeatureAccess(auth, ManagerPermission.vendorsRead);
        const hasManagersUpdatesRead = hasFeatureAccess(auth, ManagerPermission.managersManagement);
        const hasPartnersRead = hasFeatureAccess(auth, ManagerPermission.partnersRead);
        const hasProfilesRead = hasFeatureAccess(auth, ManagerPermission.profilesRead);
        const hasProductPricesRead = hasFeatureAccess(auth, ManagerPermission.productGroupPricesView);
        const hasProductDetailsRead = hasFeatureAccess(auth, ManagerPermission.productsRead);
        const hasProductVendorBranchRead = hasFeatureAccess(auth, ManagerPermission.productGroupUpdateVendorBranch);
        const hasMobileDevicesRead = hasFeatureAccess(auth, ManagerPermission.mobileDevicesRead);
        const hasBranchCodesRead = hasFeatureAccess(auth, ManagerPermission.vendorsPark);
        const tabs: Array<{ text: AuthorizationTab; counter: number }> = [];

        const transactionsTab = {
            text: AuthorizationTab.Transactions,
            counter:
                pendingAdjustmentActions && pendingAdjustmentActions.transactions > 0
                    ? Number(pendingAdjustmentActions.transactions)
                    : 0,
        };
        const beneficiariesTab = {
            text: AuthorizationTab.Beneficiaries,
            counter:
                pendingAdjustmentActions && pendingAdjustmentActions.beneficiaries > 0
                    ? Number(pendingAdjustmentActions.beneficiaries)
                    : 0,
        };
        const vendorsTab = {
            text: AuthorizationTab.VendorsDetails,
            counter:
                pendingAdjustmentActions && pendingAdjustmentActions.vendors > 0
                    ? Number(pendingAdjustmentActions.vendors)
                    : 0,
        };
        const usersTab = {
            text: AuthorizationTab.ManagerDetails,
            counter:
                pendingAdjustmentActions && pendingAdjustmentActions.users > 0
                    ? Number(pendingAdjustmentActions.users)
                    : 0,
        };
        const vendorUsersTab = {
            text: AuthorizationTab.VendorsUsersDetails,
            counter:
                pendingAdjustmentActions && pendingAdjustmentActions.vendorUsers > 0
                    ? Number(pendingAdjustmentActions.vendorUsers)
                    : 0,
        };
        const locationsTab = {
            text: AuthorizationTab.Locations,
            counter:
                pendingAdjustmentActions && pendingAdjustmentActions.locationActions > 0
                    ? Number(pendingAdjustmentActions.locationActions)
                    : 0,
        };
        const partnersTab = {
            text: AuthorizationTab.PartnersDetails,
            counter:
                pendingAdjustmentActions && pendingAdjustmentActions.partners > 0
                    ? Number(pendingAdjustmentActions.partners)
                    : 0,
        };
        const partnersUsersTab = {
            text: AuthorizationTab.PartnersUsersDetails,
            counter:
                pendingAdjustmentActions && pendingAdjustmentActions.partnerUsers > 0
                    ? Number(pendingAdjustmentActions.partnerUsers)
                    : 0,
        };
        const productsTab = {
            text: AuthorizationTab.ProductsManagement,
            counter:
                Number(pendingAdjustmentActions?.products) + Number(pendingAdjustmentActions?.productSignatures) || 0,
        };

        const productsDetailsTab = {
            text: AuthorizationTab.ProductsDetails,
            counter: Number(pendingAdjustmentActions?.productDetails) || 0,
        };

        const permissionProfilesTab = {
            text: AuthorizationTab.PermissionProfiles,
            counter:
                pendingAdjustmentActions && pendingAdjustmentActions.permissionsProfiles > 0
                    ? Number(pendingAdjustmentActions.permissionsProfiles)
                    : 0,
        };

        const mobileDevicesTab = {
            text: AuthorizationTab.MobileDevices,
            counter:
                pendingAdjustmentActions && pendingAdjustmentActions.mobileDevices > 0
                    ? Number(pendingAdjustmentActions.mobileDevices)
                    : 0,
        };

        const branchCodesTab = {
            text: AuthorizationTab.BranchCodes,
            counter:
                pendingAdjustmentActions && pendingAdjustmentActions.branchCodes > 0
                    ? Number(pendingAdjustmentActions.branchCodes)
                    : 0,
        };

        const productPriceVersionsTab = {
            text: AuthorizationTab.ProductPriceVersions,
            counter:
                pendingAdjustmentActions && pendingAdjustmentActions.productPriceVersions > 0
                    ? Number(pendingAdjustmentActions.productPriceVersions)
                    : 0,
        };

        if (hasTransactionsRead) {
            tabs.push(transactionsTab);
        }
        if (hasBeneficiaryUpdatesRead) {
            tabs.push(beneficiariesTab);
        }
        if (this.props.locationToggle && hasLocationUpdatesRead) {
            tabs.push(locationsTab);
        }
        if (hasVendorUpdatesRead) {
            tabs.push(vendorsTab);
        }
        if (hasManagersUpdatesRead) {
            tabs.push(usersTab);
        }
        if (hasVendorUsersUpdatesRead) {
            tabs.push(vendorUsersTab);
        }
        if (hasPartnersRead) {
            tabs.push(partnersTab, partnersUsersTab);
        }
        if (hasProfilesRead) {
            tabs.push(permissionProfilesTab);
        }
        if (hasProductPricesRead) {
            tabs.push(productsTab);
        }
        if (hasProductDetailsRead) {
            tabs.push(productsDetailsTab);
        }
        if (hasMobileDevicesRead) {
            tabs.push(mobileDevicesTab);
        }
        if (hasBranchCodesRead) {
            tabs.push(branchCodesTab);
        }
        if (hasProductVendorBranchRead) {
            tabs.push(productPriceVersionsTab);
        }
        return tabs;
    }

    activeMenuTab(): AuthorizationTab {
        const { location } = this.props;
        const pathname = location?.pathname || '';
        const lastUrlPart = pathname.split('/').filter(Boolean).pop() || '';

        for (const key of Object.keys(urlPathForTab)) {
            if (urlPathForTab[key] === lastUrlPart) {
                return key as AuthorizationTab;
            }
        }
    }

    renderSidemenu() {
        const tabs = this.sidemenuElements();
        const activeTab = this.activeMenuTab();
        return (
            <div style={{ marginTop: 60 }}>
                <AuthorizationSideMenu
                    activeTab={activeTab}
                    onSelectTab={this.changeActiveTab.bind(this)}
                    tabs={tabs}
                />
            </div>
        );
    }

    changeActiveTab(activeTab) {
        const url = urlPathForTab[activeTab.toString()];
        this.props.redirectToPage(`${urlPrefix}/${url}`);
    }

    render() {
        return (
            <main className={'row'}>
                <div className="col-sm-3">{this.renderSidemenu()}</div>
                <div className="col-sm-9" style={{ marginBottom: 150 }}>
                    <Outlet />
                </div>
                <div className="clearfix" />
            </main>
        );
    }
}

function mapStateToProps(state: State) {
    return {
        auth: state.auth,
        pendingAdjustmentActions: state.pendingActions.adjustments,
        locationToggle: state.appConfig.location.isActive,
    };
}
function mapDispatchToProps(dispatch: Dispatch<State>) {
    return {
        redirectToPage: (url) => dispatch(routerHelperActions.makeRedirect(url)),
        changeListType: bindActionCreators(ActionCreators.changeListType, dispatch),
    };
}

const withHooks = (Component: any) => {
    // eslint-disable-next-line react/display-name
    return (props: any) => {
        const location = useLocation();
        const params = useParams();
        const navigate = useNavigate();
        const dispatch = useDispatch();

        return (
            <Component
                {...props}
                dispatch={dispatch}
                location={location}
                navigate={navigate}
                taskType={params.taskType}
            />
        );
    };
};

export default connect(mapStateToProps, mapDispatchToProps, null)(withHooks(TaskAuthorizationsPage));
